/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface HallOfFamePeriodResponse {
  year: string;
  periods: string[];
}

export interface HallOfFamePeriod {
  year: number;
  period: string;
}

export interface HallOfFameUser {
  id: number;
  name: string;
  slug: string;
  avatar_url?: string;
  country?: string;
  points?: number;
  badges?: any[];
  current_user_permissions?: {
    follow: boolean;
  };
}

export interface HallOfFameCategory {
  title: string;
  winners: HallOfFameUser[];
}

@Injectable({
  providedIn: 'root',
})
export class HallOfFameService {
  constructor(private http: HttpClient) {}

  /**
   * Get all available periods (year/period combinations)
   * Backend returns {year: string, periods: string[]}[]
   * This method flattens it to {year: number, period: string}[]
   */
  getPeriods(): Observable<HallOfFamePeriod[]> {
    return this.http.get<HallOfFamePeriodResponse[]>('/api/v1/users/hall_of_fame/periods').pipe(
      map(response => {
        const periods: HallOfFamePeriod[] = [];
        response.forEach(yearData => {
          yearData.periods.forEach(period => {
            periods.push({
              year: parseInt(yearData.year),
              period,
            });
          });
        });
        return periods;
      }),
    );
  }

  /**
   * Get winners for a specific year and period
   * Returns an array of categories, each with title and winners
   * @param year - The year to get winners for
   * @param period - The period (e.g., Q1, Q2, Q3, Q4, H1, H2)
   */
  getWinners(year: number | string, period: string): Observable<HallOfFameCategory[]> {
    return this.http.get<HallOfFameCategory[]>(`/api/v1/users/hall_of_fame/${year}/${period}`);
  }
}
